import { Button, Link } from 'src/design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSun,
  faMoon,
  faGear,
  faRightFromBracket,
  faBars
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from 'src/design-system';
import { useAppSelector } from 'store/hooks';
import { selectUserName } from 'store/user/selectors';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { clearTokenInfo } from 'src/utils/authUtils';
import { clearImpersonationStorage } from '../store/user/userSlice';
import { UserSwitcher } from './admin/UserSwitcher';
import { resetCrispSession } from 'src/utils/crisp';

interface HeaderProps {
  onMobileMenuToggle?: () => void;
  showSignUpButton?: boolean;
}

const Header: React.FC<HeaderProps> = ({ onMobileMenuToggle, showSignUpButton = false }) => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme('dark');
  const logo = theme === 'dark' ? '/logo.svg' : '/logo-dark.svg';
  const userName = useAppSelector(selectUserName);
  const user = useAppSelector((state) => state.auth.user);
  const userPic = user?.discordAvatar ?? user?.googleAvatar;
  const { logout, loginWithRedirect, isAuthenticated } = useAuth0();

  const handleLogout = () => {
    clearTokenInfo();
    clearImpersonationStorage();
    resetCrispSession();
    logout({
      logoutParams: {
        returnTo: `${window.location.origin}/logout`
      }
    });
  };

  const handleSignUp = () => {
    loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup'
      },
      appState: {
        returnTo: window.location.pathname
      }
    });
  };

  return (
    <nav className="h-12 w-full border-b border-divider px-2">
      <div className="flex h-full items-center justify-between gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          {showSignUpButton && (
            <div className="my-1">
              <Link href="https://thedmsark.com" className="flex items-center">
                <img src={logo} alt="Logo" className="h-10 w-auto" />
                <span className="ml-2 text-sm font-bold">The DM&apos;s Ark</span>
              </Link>
            </div>
          )}

          {!showSignUpButton && (
            <Button
              isIconOnly
              variant="ghost"
              size="sm"
              onPress={onMobileMenuToggle}
              className="lg:hidden"
              aria-label="Open menu">
              <FontAwesomeIcon icon={faBars} />
            </Button>
          )}
        </div>

        <div className="flex min-w-0 items-center gap-2">
          {!showSignUpButton && user?.isAdmin && (
            <div className="hidden md:flex">
              <UserSwitcher />
            </div>
          )}

          <Button
            isIconOnly
            variant="ghost"
            size="sm"
            onPress={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
            <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />
          </Button>

          {showSignUpButton && !isAuthenticated ? (
            <Button variant="primary" size="sm" onPress={handleSignUp} className="font-medium">
              Sign Up
            </Button>
          ) : (
            <>
              <span className="hidden text-xs sm:inline">{userName}</span>
              <details className="relative">
                <summary className="list-none cursor-pointer">
                  {userPic ? (
                    <img
                      src={userPic}
                      alt={userName ?? 'User'}
                      className="h-8 w-8 rounded-full border border-default-300"
                    />
                  ) : (
                    <div className="flex h-8 w-8 items-center justify-center rounded-full border border-default-300 bg-default-100 text-xs font-semibold">
                      {(userName ?? 'U').slice(0, 1).toUpperCase()}
                    </div>
                  )}
                </summary>
                <div className="absolute right-0 z-50 mt-2 w-44 rounded-lg border bg-background p-1 shadow-lg">
                  <button
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-default-100"
                    onClick={() => navigate('/settings')}
                    type="button">
                    <FontAwesomeIcon icon={faGear} />
                    Settings
                  </button>
                  <button
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-danger hover:bg-danger/10"
                    onClick={handleLogout}
                    type="button">
                    <FontAwesomeIcon icon={faRightFromBracket} />
                    Logout
                  </button>
                </div>
              </details>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Header;
